import { useState } from "react";
import { useTranslation } from "react-i18next";

import {
  canControlRun,
  type CcRunState,
  type CcRunStatus,
  isRunTerminal,
} from "@/api/code-companion";
import { useAdtStatus } from "@/hooks/useApi";
import { useAdtLogonFlow } from "@/hooks/useAdtLogonFlow";
import { useCurrentUser } from "@/hooks/useAuth";
import { formatCost, minutesUntil } from "@/lib/code-companion-session";
import {
  useCcCancelRun,
  useCcCreateRun,
  useCcDrafting,
  useCcPauseRun,
  useCcResumeRun,
  useCcRun,
} from "@/hooks/useCodeCompanion";
import { tp } from "@/lib/plural";

import DraftingProgress from "./DraftingProgress";
import { ADT_EXTENSION_STORE_URL } from "@/lib/adt-extension";
import { errorDetail } from "@/lib/api-error";
import { pauseMessage } from "@/lib/pause-reason";

import { answerWindow } from "@/lib/code-companion-session";

/**
 * Painel do run autônomo (S-060, fatia 7).
 *
 * O run roda num worker, noutro processo. Esta tela **observa**: ela nunca dirige um
 * passo. Os botões pedem transição de estado e devolvem na hora; quem trabalha é o
 * `cc-worker`. Confundir as duas coisas foi o que fez o resume do HITL antigo precisar
 * segurar o grafo dentro do handler HTTP.
 *
 * A regra que a tela precisa acertar é distinguir **parou esperando você** de **parou
 * por erro**, porque a ação é diferente em cada caso.
 */

/** O que oferecer ao usuário, dado o estado. Exportado para poder ser testado puro. */
export function availableActions(status: CcRunStatus): Array<"pause" | "resume" | "cancel"> {
  if (isRunTerminal(status)) return [];
  if (status === "paused") return ["resume", "cancel"];
  if (status === "cancelling") return [];
  return ["pause", "cancel"];
}


/**
 * Os objetos que valem uma retentativa: os que **não** concluíram (S-064 onda 3).
 *
 * Não é "os que falharam": objeto que ficou pendente porque o run parou no meio também
 * precisa rodar de novo, e ele não tem fase reprovada nenhuma. O critério é o mesmo que
 * a matriz usa para pintar a linha e o mesmo que decide o desfecho do run.
 *
 * Retentar **só** o que faltou, e não o pacote inteiro, evita reescrever no SAP objetos
 * que já estão verdes: cada reescrita consome um transporte e um lock no alvo.
 */
export function retryTargets(
  run: Pick<CcRunState, "objects">,
): Array<{ object_name: string; object_type: string }> {
  return (run.objects ?? [])
    .filter((o) => !o.concluded)
    .map((o) => ({ object_name: o.object_name, object_type: o.object_type }));
}

/**
 * As opções que a pergunta ofereceu, como lista de strings.
 *
 * ✅ **CC.34 F6.** Elas existiam no payload desde a onda 5 e eram despejadas num `<pre>`
 * junto com o resto — o único widget era um `<textarea>` de prosa, e o corpo enviado era
 * sempre `{text: "…"}`, nunca `{option: "stop_run"}`. Do outro lado, `resume_run`
 * retomava incondicionalmente. Responder "parar" e o run seguir deployando era o oposto
 * do que a pessoa pediu.
 *
 * Pergunta de resposta fechada precisa de botão: prosa vira resposta que ninguém
 * reconcilia, e o backend agora recusa uma opção que não foi oferecida.
 */
export function questionOptions(question: Record<string, unknown> | null | undefined): string[] {
  const raw = question?.options;
  if (!Array.isArray(raw)) return [];
  return raw.filter((o): o is string => typeof o === "string" && !!o.trim());
}

/** Rótulo legível de uma opção. Desconhecida aparece como veio, em vez de sumir. */
export function optionLabel(t: (k: string) => string, option: string): string {
  if (option === "skip_object") return t("Continue without this object");
  if (option === "stop_run") return t("Stop the run here");
  return option;
}

/** Percentual concluído. Run sem objeto é 0, nunca 100: vazio não é entrega. */
export function progressPct(run: Pick<CcRunState, "total_objects" | "concluded_objects">): number {
  if (!run.total_objects) return 0;
  return Math.round((100 * run.concluded_objects) / run.total_objects);
}

const TONE: Record<string, { bg: string; fg: string }> = {
  running: { bg: "#eef5ff", fg: "#2a5db0" },
  paused: { bg: "#fff8e6", fg: "#96690d" },
  completed: { bg: "#f3faf6", fg: "#2b8f57" },
  partial: { bg: "#fff8e6", fg: "#96690d" },
  failed: { bg: "#fdf2f2", fg: "#b02a31" },
  cancelled: { bg: "#f5f6f8", fg: "#6b7280" },
  cancelling: { bg: "#f5f6f8", fg: "#6b7280" },
  queued: { bg: "#f5f6f8", fg: "#6b7280" },
};

interface Props {
  projectId: string;
  runId: string;
}

export default function RunPanel({ projectId, runId }: Props) {
  const { t } = useTranslation();
  const { data: run, isLoading, isError } = useCcRun(projectId, runId);
  // F6: pausar, retomar e cancelar exigem `consultant`. Quem só acompanha vê o
  // progresso e não vê controles, em vez de clicar e tomar 403.
  const mayControl = canControlRun(useCurrentUser()?.role);
  const pause = useCcPauseRun(projectId, runId);
  const resume = useCcResumeRun(projectId, runId);
  const cancel = useCcCancelRun(projectId, runId);
  const [answer, setAnswer] = useState("");
  // O roster nomeia os objetos que esperam um dev; o resumo sozinho só os conta.
  const drafting = useCcDrafting(projectId, run?.package_id, {
    live: !!run && !isRunTerminal(run.status),
  });

  // A primeira recusa entre as três ações. Uma linha só: elas são mutuamente
  // exclusivas na prática, e três caixas de erro empilhadas seriam ruído.
  const recusa = errorDetail(pause.error ?? resume.error ?? cancel.error);
  const actionError =
    recusa === null ? null : recusa || t("The action was refused and no reason came back.");
  // O pacote vem do próprio run: o painel recebe `runId`, e disparar é operação de
  // pacote. Sem isto o botão precisaria que a página descesse o `packageId` até aqui.
  const retry = useCcCreateRun(projectId, run?.package_id);
  // ⚠️ **O relógio mora no card onde se responde** (#577). Ele já existia no
  // `EscalationPanel`, e aquele painel é histórico: as opções de lá são `<span>`. Quem
  // decide clica nos botões `hitl-${op}` daqui, e era justamente aqui que não havia
  // relógio, aviso nem caminho de reconectar. Medido em DEV, 26/08: a resposta veio 26
  // minutos depois da pausa e a sessão expirou 57 segundos depois.
  const { data: adtSession, isLoading: loadingSession } = useAdtStatus(projectId);
  const minutesLeft = minutesUntil(adtSession?.expires_at);
  // Enquanto não se leu, não se afirma: empurrar reconexão sem ter lido invalidaria a
  // sessão que o run está segurando. Mesma guarda da ConductorStrip.
  const questionWindow = loadingSession ? "ok" : answerWindow(adtSession?.status, minutesLeft);
  // #593: o par pedir-reentrance-e-abrir sai daqui e vai para o hook. Esta era a quarta
  // cópia dele, nascida na PR #598, e a que tinha menos guarda: erro engolido no `onClick`
  // e nenhuma detecção de extensão, num botão que aparece justo quando alguém decide com
  // o relógio da sessão correndo.
  //
  // `enabled` calculado aqui, e não deduzido pelo hook: "o logon está sendo oferecido" só
  // faz sentido com a pergunta aberta e a janela apertada, que é dado desta tela.
  const offersReconnect =
    !!run && run.status === "paused" && !!run.question && questionWindow !== "ok";
  const adtLogon = useAdtLogonFlow(projectId, { enabled: offersReconnect });

  if (isError) {
    return (
      <div className="rounded-xl border border-border bg-card p-6 text-sm text-muted-foreground">
        {t("Could not read this run.")}
      </div>
    );
  }

  if (isLoading || !run) {
    return (
      <div className="rounded-xl border border-border bg-card p-6 text-sm text-muted-foreground">
        {t("Loading run...")}
      </div>
    );
  }

  const tone = TONE[run.status] ?? TONE.queued;

  return (
    <div className="overflow-hidden rounded-xl border border-border bg-card shadow-sm">
      <div className="flex flex-wrap items-center gap-3 border-b border-border px-4 py-3">
        <span
          className="rounded px-2 py-0.5 font-mono text-[10px] font-semibold uppercase"
          style={{ background: tone.bg, color: tone.fg }}
        >
          {run.status}
        </span>
        <span className="text-sm">
          {t("{{done}} of {{total}} objects", {
            done: run.concluded_objects,
            total: run.total_objects,
          })}
        </span>
        <div className="h-1.5 w-32 overflow-hidden rounded-full bg-muted">
          <div
            className="h-full rounded-full"
            style={{ width: `${progressPct(run)}%`, background: tone.fg }}
          />
        </div>
        <div className="flex-1" />
        {run.actual_cost_usd > 0 && (
          <span className="font-mono text-[11px] text-muted-foreground">
            {/* Mesmo formatador da faixa e da trilha: com `toFixed(2)` este card
                mostrava US$ 0.01 para o run que a faixa mostrava $0.0090. */}
            {formatCost(run.actual_cost_usd)}
          </span>
        )}
      </div>

      {/* CC.34 F3: a fase de montar o change set. Ela vem ANTES de haver objeto na
          matriz, então sem este bloco os primeiros minutos de um run grande mostram
          "0 de 0 objetos" — indistinguível de travado, e é a fase mais longa.
          
          Fica **só aqui**, e não também na página: a primeira versão renderizava nos
          dois lugares e a Trace mostrava a mesma barra duas vezes, uma acima da outra.
          O resumo vem do run (chega junto com o resto do painel) e a lista do roster,
          que é o que nomeia quem espera um dev. */}
      {run.drafting && run.drafting.total > 0 && (
        <div className="border-b border-border px-4 py-3">
          <DraftingProgress summary={run.drafting} items={drafting.data?.items} />
        </div>
      )}

      {run.status === "paused" && (
        <div className="space-y-2 border-b border-border bg-amber-50/50 px-4 py-3 text-sm dark:bg-amber-950/20">
          <div>
            <strong className="font-semibold">{t("Paused")}</strong>{" "}
            {/* ⚠️ **A frase sai do MOTIVO, não do remédio** (review da #617). Escolher
                pela ação colapsa dois eixos diferentes: `manual` e `model_unavailable`
                pedem a mesma coisa (retomar) e precisam de frases distintas, porque a
                pessoa precisa saber **o que** resolver antes de retomar. Enquanto os
                dois eixos estavam colados, o run parado por Bedrock fora exibia "Paused
                manually.", que é falso e ainda sugere que ninguém precisa agir. */}
            <span data-testid="pause-banner" className="text-muted-foreground">
              {pauseMessage(t, run.pause_reason)}
            </span>
          </div>

          {/* A pergunta já vem no payload e não era renderizada: o run parava em HITL
              e o navegador não tinha como destravar. */}
          {run.question && mayControl && (
            <div className="space-y-2 rounded-md border border-border bg-card p-3">
              <div className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
                {String(run.question.kind ?? "hitl")}
              </div>
              {typeof run.question.text === "string" && (
                <p className="text-xs">{run.question.text}</p>
              )}
              <pre className="max-h-32 overflow-auto whitespace-pre-wrap break-words text-[11px] text-muted-foreground">
                {Object.entries(run.question)
                  .filter(([k]) => k !== "kind" && k !== "options" && k !== "text")
                  .map(([k, v]) => `${k}: ${typeof v === "string" ? v : JSON.stringify(v)}`)
                  .join("\n")}
              </pre>
              <textarea
                value={answer}
                onChange={(e) => setAnswer(e.target.value)}
                placeholder={t("Note (optional)")}
                className="h-14 w-full rounded border border-border bg-background p-2 text-xs"
              />
              {/* #577: a credencial que autoriza a escrita tem relógio, e ele corre
                  durante a pausa. Avisa sem travar: a pessoa pode ter reconectado em
                  outra aba, e bloquear o botão viraria beco sem saída. */}
              {questionWindow === "ok" && minutesLeft !== null && (
                <div
                  data-testid="question-session-clock"
                  className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground"
                >
                  {t("{{mins}} min of ADT session left", { mins: minutesLeft })}
                </div>
              )}
              {questionWindow !== "ok" && (
                <div
                  data-testid="question-session-warning"
                  className="flex flex-wrap items-center gap-2 rounded border border-amber-500/40 bg-amber-500/10 px-2 py-1.5 text-[11px] text-amber-700 dark:text-amber-400"
                >
                  <span>
                    {questionWindow === "gone"
                      ? t("The ADT session is down: reconnect before answering.")
                      : t("Only {{mins}} min of ADT session left: reconnect before answering.", {
                          mins: minutesLeft ?? 0,
                        })}
                  </span>
                  <button
                    type="button"
                    data-testid="question-reconnect"
                    onClick={adtLogon.start}
                    disabled={adtLogon.isPending}
                    className="rounded border border-amber-500/50 px-2 py-0.5 font-mono text-[10px] uppercase text-amber-700 hover:bg-amber-500/20 disabled:opacity-50 dark:text-amber-400"
                  >
                    {t("Reconnect ADT")}
                  </button>
                  {/* #593: em host publicado o reentrance volta por loopback, e sem a
                      extensão o ticket morre no SADT_RESOURCE 034 sem pista da causa
                      (S-070). `undefined` é "ainda procurando": não se pede instalação
                      a quem talvez já tenha. */}
                  {adtLogon.needsExtension && adtLogon.extension === null && (
                    <a
                      data-testid="question-ext-install"
                      href={ADT_EXTENSION_STORE_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-mono text-[10px] uppercase underline"
                    >
                      {t("Install the extension")}
                    </a>
                  )}
                </div>
              )}
              {/* A recusa do logon precisa aparecer: antes ela era uma promise rejeitada
                  dentro do onClick, e o botão morria sem nada na tela. `""` significa que
                  houve erro sem texto, e aí entra a frase traduzida. */}
              {adtLogon.error !== null && (
                <div
                  data-testid="question-logon-error"
                  className="rounded border border-destructive/40 bg-destructive/10 px-2 py-1.5 text-[11px] text-destructive"
                >
                  {adtLogon.error || t("Could not start the ADT logon.")}
                </div>
              )}
              {/* Um botão por opção. A escolha vai como `option`, e a nota como `text`:
                  o backend decide pela primeira e guarda a segunda como evidência. */}
              <div className="flex flex-wrap gap-2">
                {questionOptions(run.question).map((op) => (
                  <button
                    key={op}
                    type="button"
                    data-testid={`hitl-${op}`}
                    disabled={resume.isPending}
                    onClick={() =>
                      resume.mutate({
                        option: op,
                        ...(answer.trim() ? { text: answer.trim() } : {}),
                      })
                    }
                    className="rounded border border-border px-2.5 py-1 text-[11px] font-semibold text-foreground hover:bg-muted disabled:opacity-50"
                  >
                    {optionLabel(t, op)}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* A recusa do backend precisa aparecer. Sem isto, quem digita a decisão do HITL e
          clica em `resume` vê o botão piscar e o run continuar pausado — sem erro, sem
          toast, sem nada. O detail do 409/422 diz exatamente o que resolver, e era
          descartado. */}
      {actionError && (
        <div
          data-testid="run-action-error"
          className="mx-4 mb-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-300"
        >
          {actionError}
        </div>
      )}

      {/* #576: o motivo do desfecho. O backend grava a frase em todo caminho terminal e
          devolve null em run completo, então a caixa só existe quando há o que dizer. */}
      {isRunTerminal(run.status) && run.error && (
        <div
          data-testid="run-outcome-reason"
          className="mx-4 mb-3 rounded border border-border bg-muted/40 px-3 py-2 text-xs text-foreground"
        >
          {run.error}
        </div>
      )}

      <div className="flex gap-2 px-4 py-3">
        {!mayControl ? (
          <span className="text-xs text-muted-foreground">
            {t("You can follow this run, but not control it.")}
          </span>
        ) : availableActions(run.status).length === 0 ? (
          <span className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            {isRunTerminal(run.status) ? t("This run has finished.") : t("Finishing...")}
            {/* S-064 onda 3: sem isto, depois de um `partial` não havia como tentar de
                novo. Disparar outro run terminava na hora, porque as fases já estão no
                ledger e objeto reprovado é terminal — medido ao vivo em 19/08. */}
            {isRunTerminal(run.status) && retryTargets(run).length > 0 && (
              <button
                type="button"
                data-testid="retry-run"
                disabled={retry.isPending}
                onClick={() => retry.mutate({ retry: true, only: retryTargets(run) })}
                className="rounded border border-border px-2.5 py-1 font-mono text-[11px] uppercase text-foreground hover:bg-muted disabled:opacity-50"
              >
                {tp(t, retryTargets(run).length, "retry 1 object", "retry {{n}} objects")}
              </button>
            )}
          </span>
        ) : (
          availableActions(run.status).map((action) => (
            <button
              key={action}
              type="button"
              disabled={pause.isPending || resume.isPending || cancel.isPending}
              onClick={() => {
                if (action === "pause") pause.mutate("manual");
                if (action === "resume")
                  resume.mutate(answer.trim() ? { text: answer.trim() } : undefined);
                if (action === "cancel") cancel.mutate();
              }}
              className="rounded border border-border px-2.5 py-1 font-mono text-[11px] uppercase text-foreground hover:bg-muted disabled:opacity-50"
            >
              {action}
            </button>
          ))
        )}
      </div>
    </div>
  );
}
